import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  X, Loader, Save, RefreshCw, FileText, Trash2, Undo2, Pencil, Paperclip, Lock, KeyRound, ShieldCheck, Upload, CheckCircle2, Search,
} from 'lucide-react';
import { fmtDate, fmtMoney, labelStyle, inputStyle, th, td, GREEN, AMBER, RED } from '../utils/atoShared';
import { todayIso } from '../utils/atoPlan';
import { vaultCall as call, checkVaultFile, expandZips, prepareVaultFile, useVault, openVaultFile, VaultLock, ChangePassword } from './AtoVault';

// ── Tax documents ───────────────────────────────────────────────────────────────
// Every other piece of tax paper for Paul, Holly and the businesses: ATO letters
// ("Your PAYG instalments have changed"), assessments, registrations, agent advice,
// ASIC / Revenue NSW notices. Same vault as Tax returns: same password, same 15-minute
// token, encrypted at rest (settings KV `ato_tax_docs` + `taxdoc-<id>/` in the bucket).
//
// Drop several files at once: each is read by Claude (who it's for, who sent it, the
// heading, the date, the key figure, whether anything has to be done) and saved as its
// own record straight away. A .zip is unpacked in the browser first; a big file goes up
// in pieces (AtoVault prepareVaultFile). A file Claude can't read is still saved, as "Unsorted", so
// nothing dropped is ever lost. Nothing is deleted: Remove hides, Restore brings back.

const DOC_TYPES = ['Letter', 'Notice', 'Notice of assessment', 'Statement', 'Activity statement', 'Registration', 'Email', 'Agent advice', 'Other'];
const ISO = /^\d{4}-\d{2}-\d{2}$/;
const blank = () => ({ entity: '', abn: '', from: 'ATO', docType: 'Letter', subject: '', date: todayIso(), year: '', reference: '', amount: '', amountLabel: '', summary: '', actionNeeded: false, actionDue: '', action: '', actionDone: '', notes: '' });

const fromExtract = (x, name) => ({
  ...blank(),
  entity: String(x.entity || '').trim() || 'Unsorted',
  abn: String(x.abn || '').replace(/\D/g, ''),
  from: x.from || 'ATO',
  docType: DOC_TYPES.includes(x.docType) ? x.docType : 'Letter',
  subject: String(x.subject || '').trim() || name.replace(/\.[a-z0-9]+$/i, ''),
  date: ISO.test(x.date || '') ? x.date : '',
  year: x.year || '',
  reference: x.reference || '',
  amount: Number(x.amount) ? Number(x.amount) : '',
  amountLabel: x.amountLabel || '',
  summary: x.summary || '',
  actionNeeded: !!x.actionNeeded,
  actionDue: ISO.test(x.actionDue || '') ? x.actionDue : '',
  action: x.action || '',
});

const drawerWrap = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' };
const drawerBody = { width: 560, maxWidth: '100%', background: 'var(--hp-bg)', borderLeft: '1px solid var(--hp-border)', height: '100%', overflowY: 'auto', padding: 24 };
const dropZone = { border: '2px dashed var(--hp-border)', borderRadius: 10, padding: 16, textAlign: 'center' };
const kicker = { fontFamily: 'var(--font-mono)', fontSize: '.65rem', textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--hp-gold)', marginBottom: 4 };
const pill = (color) => ({ fontSize: '.7rem', fontWeight: 700, color, border: `1px solid ${color}`, borderRadius: 999, padding: '1px 8px', whiteSpace: 'nowrap' });

function ActionPill({ d, today }) {
  if (!d.actionNeeded) return <span style={{ color: 'var(--hp-dim)' }}>—</span>;
  if (d.actionDone) return <span style={pill(GREEN)}>Done {fmtDate(d.actionDone)}</span>;
  const late = d.actionDue && d.actionDue < today;
  return <span style={pill(late ? RED : AMBER)} title={d.action}>{d.actionDue ? `${late ? 'Overdue' : 'Action by'} ${fmtDate(d.actionDue)}` : 'Action needed'}</span>;
}

// ── edit a document ─────────────────────────────────────────────────────────────
function DocDrawer({ doc, entities, unlock, onLocked, onClose, onSaved, notify }) {
  const [f, setF] = useState(() => ({ ...blank(), ...doc }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await call('saveCorr', { unlock, doc: f });
      if (r.error === 'VAULT_LOCKED') { onLocked(); return; }
      if (!r.success) { notify?.(r.error || 'Save failed', 'error'); return; }
      notify?.('Saved', 'success');
      onSaved(r.doc);
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const field = (k, label, type = 'text', extra = {}) => (
    <div><label style={labelStyle}>{label}</label>
      <input type={type} step={type === 'number' ? '0.01' : undefined} className="input" style={inputStyle} value={f[k] ?? ''} onChange={(e) => set(k, e.target.value)} {...extra} /></div>
  );

  return (
    <div style={drawerWrap} onClick={onClose}>
      <div style={drawerBody} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div><div style={kicker}>Tax document</div><div style={{ fontWeight: 800, fontSize: '1.05rem' }}>{doc.subject || 'Document'}</div></div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Entity</label>
            <input className="input" style={inputStyle} list="atd-entities" value={f.entity} onChange={(e) => set('entity', e.target.value)} />
            <datalist id="atd-entities">{entities.map((e) => <option key={e} value={e} />)}</datalist>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>{field('subject', 'Subject')}</div>
          {field('from', 'From')}
          <div><label style={labelStyle}>Type</label>
            <select className="input" style={inputStyle} value={f.docType} onChange={(e) => set('docType', e.target.value)}>{DOC_TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
          {field('date', 'Dated', 'date')}
          {field('year', 'Income year', 'text', { placeholder: '2026–27' })}
          {field('reference', 'Their reference')}
          {field('abn', 'ABN')}
          {field('amount', 'Key figure ($)', 'number')}
          {field('amountLabel', 'What the figure is')}
          <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Summary</label>
            <textarea className="input" style={{ ...inputStyle, minHeight: 70 }} value={f.summary} onChange={(e) => set('summary', e.target.value)} /></div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.88rem' }}>
              <input type="checkbox" checked={!!f.actionNeeded} onChange={(e) => set('actionNeeded', e.target.checked)} /> Something has to be done
            </label>
          </div>
          {f.actionNeeded && <>
            <div style={{ gridColumn: '1 / -1' }}>{field('action', 'What has to be done')}</div>
            {field('actionDue', 'Due by', 'date')}
            {field('actionDone', 'Done on', 'date')}
          </>}
          <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Notes</label>
            <textarea className="input" style={{ ...inputStyle, minHeight: 60 }} value={f.notes} onChange={(e) => set('notes', e.target.value)} /></div>
        </div>
        <p style={{ color: 'var(--hp-dim)', fontSize: '.78rem' }}>Don’t type a TFN into any field. It’s on the PDF, and the PDF is stored encrypted.</p>
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? <Loader size={14} className="spin" /> : <Save size={14} />} Save</button>
      </div>
    </div>
  );
}

// ── the tab ─────────────────────────────────────────────────────────────────────
export default function TaxDocsTab({ notify, knownEntities = [] }) {
  const { status, unlock, lock: vaultLock, onUnlocked } = useVault(notify);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [queue, setQueue] = useState([]);        // [{ name, state: 'reading'|'saving'|'done'|'error', note }]
  const [drawer, setDrawer] = useState(null);
  const [changing, setChanging] = useState(false);
  const [opening, setOpening] = useState('');
  const [entity, setEntity] = useState('');
  const [year, setYear] = useState('');
  const [q, setQ] = useState('');
  const [onlyAction, setOnlyAction] = useState(false);
  const [showRemoved, setShowRemoved] = useState(false);
  const today = todayIso();

  const lock = useCallback(() => { vaultLock(); setDocs([]); setDrawer(null); }, [vaultLock]);
  // Locked by the 15-minute timer too: don't keep decrypted records in memory.
  useEffect(() => { if (!unlock) { setDocs([]); setDrawer(null); } }, [unlock]);

  const load = useCallback(async () => {
    if (!unlock) return;
    setLoading(true);
    try {
      const r = await call('listCorr', { unlock });
      if (r.error === 'VAULT_LOCKED') { lock(); return; }
      if (r.success) setDocs(r.docs); else notify?.(r.error || 'Could not load documents', 'error');
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setLoading(false); }
  }, [unlock, lock, notify]);
  useEffect(() => { load(); }, [load]);

  const upsert = (d) => setDocs((ds) => (ds.some((x) => x.id === d.id) ? ds.map((x) => (x.id === d.id ? d : x)) : [...ds, d]));

  // One file at a time: read → save with the file. Sequential, so a stack of letters
  // doesn't fire a dozen Claude reads at once, and each record lands as it's done.
  const ingest = async (fileList) => {
    const files = (await expandZips(fileList, notify)).filter((f) => checkVaultFile(f, notify));
    if (!files.length) return;
    setQueue(files.map((f) => ({ name: f.name, state: 'waiting' })));
    const mark = (i, patch) => setQueue((qs) => qs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
    let saved = 0;
    for (let i = 0; i < files.length; i++) {
      const fl = files[i];
      try {
        mark(i, { state: 'reading' });
        const prep = await prepareVaultFile(fl, { unlock, onProgress: (n) => mark(i, { note: n }) });
        const r = prep.readBase64 ? await call('readCorr', { unlock, base64: prep.readBase64, contentType: prep.contentType }) : { success: false };
        if (r.error === 'VAULT_LOCKED') { lock(); return; }
        const rec = r.success ? fromExtract(r.extracted || {}, fl.name) : fromExtract({}, fl.name);
        mark(i, { state: 'saving' });
        const s = await call('saveCorr', { unlock, doc: rec, ...(await prep.upload()), fileKind: rec.docType });
        if (s.error === 'VAULT_LOCKED') { lock(); return; }
        if (!s.success) { mark(i, { state: 'error', note: s.error }); continue; }
        upsert(s.doc); saved++;
        mark(i, { state: 'done', note: r.success ? `${s.doc.entity} · ${s.doc.subject}` : 'Saved as Unsorted: couldn’t read it, fill it in' });
      } catch (e) {
        if (e.message === 'VAULT_LOCKED') { lock(); return; }
        mark(i, { state: 'error', note: e.message });
      }
    }
    notify?.(`${saved} of ${files.length} saved`, saved === files.length ? 'success' : 'info');
  };

  const addFile = async (d, fl) => {
    if (!checkVaultFile(fl, notify)) return;
    try {
      const prep = await prepareVaultFile(fl, { unlock });
      const r = await call('addCorrFile', { unlock, id: d.id, ...(await prep.upload()) });
      if (r.error === 'VAULT_LOCKED') { lock(); return; }
      if (r.success) { upsert(r.doc); notify?.('File added', 'success'); } else notify?.(r.error || 'Upload failed', 'error');
    } catch (e) { if (e.message === 'VAULT_LOCKED') lock(); else notify?.(e.message, 'error'); }
  };
  const openFile = async (d, file) => {
    setOpening(file.id);
    try { await openVaultFile('getCorrFile', { id: d.id, docId: file.id }, { unlock, onLocked: lock, notify }); }
    finally { setOpening(''); }
  };
  const save = async (d, patch, msg) => {
    const r = await call('saveCorr', { unlock, doc: { ...d, ...patch } });
    if (r.error === 'VAULT_LOCKED') { lock(); return; }
    if (r.success) { upsert(r.doc); notify?.(msg, 'success'); } else notify?.(r.error || 'Failed', 'error');
  };
  const toggleRemoved = async (d, deleted) => {
    const r = await call('setCorrDeleted', { unlock, id: d.id, deleted });
    if (r.error === 'VAULT_LOCKED') { lock(); return; }
    if (!r.success) { notify?.(r.error || 'Failed', 'error'); return; }
    upsert(r.doc);
    notify?.(deleted ? 'Removed — Restore is under “Show removed”' : 'Restored', 'success');
  };

  const entities = useMemo(() => Array.from(new Set([...knownEntities, ...docs.map((d) => d.entity)].filter(Boolean))).sort(), [knownEntities, docs]);
  const years = useMemo(() => Array.from(new Set(docs.map((d) => d.year).filter(Boolean))).sort().reverse(), [docs]);
  const open = docs.filter((d) => !d.deletedAt && d.actionNeeded && !d.actionDone);
  const removedCount = docs.filter((d) => d.deletedAt).length;
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return docs
      .filter((d) => showRemoved || !d.deletedAt)
      .filter((d) => !entity || d.entity === entity)
      .filter((d) => !year || d.year === year)
      .filter((d) => !onlyAction || (d.actionNeeded && !d.actionDone))
      .filter((d) => !needle || [d.entity, d.from, d.subject, d.summary, d.reference, d.notes, d.docType].join(' ').toLowerCase().includes(needle))
      .sort((a, b) => String(b.date).localeCompare(String(a.date)) || a.entity.localeCompare(b.entity));
  }, [docs, showRemoved, entity, year, onlyAction, q]);
  const busy = queue.some((x) => x.state === 'waiting' || x.state === 'reading' || x.state === 'saving');

  if (!status) return <div style={{ color: 'var(--hp-dim)' }}><Loader size={14} className="spin" /> Checking the vault…</div>;
  if (!unlock) return <VaultLock hasPassword={status.hasPassword} onUnlocked={onUnlocked} notify={notify} what="Tax documents" />;

  return (
    <>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: GREEN, fontSize: '.82rem', fontWeight: 600 }}><ShieldCheck size={15} /> Unlocked · encrypted at rest</span>
        <div style={{ flex: 1 }} />
        <button className="btn btn-ghost btn-sm" onClick={() => setChanging((c) => !c)}><KeyRound size={14} /> Password</button>
        <button className="btn btn-secondary btn-sm" onClick={load} title="Refresh"><RefreshCw size={14} /></button>
        <button className="btn btn-secondary btn-sm" onClick={lock}><Lock size={14} /> Lock</button>
      </div>

      {changing && <ChangePassword notify={notify} onClose={() => setChanging(false)} onDone={(t) => { onUnlocked(t); setChanging(false); }} />}

      <div className="card" style={{ ...dropZone, marginBottom: 14 }} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); if (!busy) ingest(e.dataTransfer?.files); }}>
        <label style={{ cursor: busy ? 'wait' : 'pointer', display: 'block' }}>
          <div style={{ fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 8 }}><Upload size={16} /> Drop ATO letters and tax documents here</div>
          <div style={{ color: 'var(--hp-dim)', fontSize: '.8rem', marginTop: 4 }}>Several at once, or a .zip · Claude files each one by who it’s for, who sent it, the date and anything you need to do</div>
          <input type="file" multiple accept="application/pdf,image/*,.zip,application/zip" style={{ display: 'none' }} disabled={busy} onChange={(e) => { ingest(e.target.files); e.target.value = ''; }} />
        </label>
        {queue.length > 0 && (
          <div style={{ marginTop: 12, textAlign: 'left', display: 'grid', gap: 4 }}>
            {queue.map((x, i) => (
              <div key={i} style={{ fontSize: '.8rem', display: 'flex', gap: 8, alignItems: 'center' }}>
                {x.state === 'done' ? <CheckCircle2 size={13} color={GREEN} /> : x.state === 'error' ? <X size={13} color={RED} /> : <Loader size={13} className={x.state === 'waiting' ? '' : 'spin'} />}
                <span style={{ fontWeight: 600 }}>{x.name}</span>
                <span style={{ color: x.state === 'error' ? RED : 'var(--hp-dim)' }}>{x.note || (x.state === 'reading' ? 'reading…' : x.state === 'saving' ? 'saving…' : x.state === 'waiting' ? 'waiting' : '')}</span>
              </div>
            ))}
            {!busy && <button className="btn btn-ghost btn-sm" style={{ justifySelf: 'start' }} onClick={() => setQueue([])}>Clear</button>}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <select className="input" style={{ ...inputStyle, width: 'auto' }} value={entity} onChange={(e) => setEntity(e.target.value)}>
          <option value="">Every entity</option>{entities.map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
        <select className="input" style={{ ...inputStyle, width: 'auto' }} value={year} onChange={(e) => setYear(e.target.value)}>
          <option value="">Every year</option>{years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <div style={{ position: 'relative', flex: '1 1 200px', maxWidth: 320 }}>
          <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--hp-dim)' }} />
          <input className="input" style={{ ...inputStyle, paddingLeft: 28 }} placeholder="Search subject, summary, reference…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <label style={{ fontSize: '.8rem', display: 'flex', alignItems: 'center', gap: 6, color: open.length ? AMBER : 'var(--hp-dim)' }}>
          <input type="checkbox" checked={onlyAction} onChange={(e) => setOnlyAction(e.target.checked)} /> Needs action ({open.length})
        </label>
        {removedCount > 0 && (
          <label style={{ fontSize: '.8rem', color: 'var(--hp-dim)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={showRemoved} onChange={(e) => setShowRemoved(e.target.checked)} /> Show removed ({removedCount})
          </label>
        )}
      </div>

      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        {loading ? <div style={{ padding: 20, color: 'var(--hp-dim)' }}><Loader size={14} className="spin" /> Loading…</div> : !shown.length ? (
          <div style={{ padding: 24, color: 'var(--hp-dim)' }}>{docs.length ? 'Nothing matches those filters.' : 'No documents yet. Drop the ATO letters above.'}</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
            <thead><tr>
              <th style={th}>Dated</th><th style={th}>Entity</th><th style={th}>Document</th><th style={{ ...th, textAlign: 'right' }}>Key figure</th>
              <th style={th}>Action</th><th style={th}>Files</th><th style={th} />
            </tr></thead>
            <tbody>
              {shown.map((d) => (
                <tr key={d.id} style={{ opacity: d.deletedAt ? 0.5 : 1 }}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{d.date ? fmtDate(d.date) : '—'}</td>
                  <td style={td}><div style={{ fontWeight: 600, color: d.entity === 'Unsorted' ? AMBER : undefined }}>{d.entity}</div>{d.year && <div style={{ color: 'var(--hp-dim)', fontSize: '.74rem' }}>{d.year}</div>}</td>
                  <td style={{ ...td, maxWidth: 380 }}>
                    <div style={{ fontWeight: 600 }}>{d.subject}</div>
                    <div style={{ color: 'var(--hp-dim)', fontSize: '.74rem' }}>{[d.from, d.docType, d.reference && `ref ${d.reference}`].filter(Boolean).join(' · ')}</div>
                    {d.summary && <div style={{ fontSize: '.8rem', marginTop: 3 }}>{d.summary}</div>}
                  </td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {d.amount === '' || d.amount == null ? '—' : <>{fmtMoney(d.amount)}{d.amountLabel && <div style={{ color: 'var(--hp-dim)', fontSize: '.72rem' }}>{d.amountLabel}</div>}</>}
                  </td>
                  <td style={td}>
                    <ActionPill d={d} today={today} />
                    {d.actionNeeded && d.action && <div style={{ color: 'var(--hp-dim)', fontSize: '.72rem', marginTop: 3 }}>{d.action}</div>}
                    {d.actionNeeded && !d.actionDone && !d.deletedAt && (
                      <button className="btn btn-ghost btn-sm" style={{ padding: '1px 6px', marginTop: 3 }} onClick={() => save(d, { actionDone: today }, 'Marked done')}><CheckCircle2 size={12} /> Done</button>
                    )}
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {(d.files || []).map((fl) => (
                        <button key={fl.id} className="btn btn-ghost btn-sm" style={{ justifyContent: 'flex-start', padding: '2px 6px' }} onClick={() => openFile(d, fl)} title={fl.name}>
                          {opening === fl.id ? <Loader size={12} className="spin" /> : <FileText size={12} />} {fl.kind || 'File'}
                        </button>
                      ))}
                      <label className="btn btn-ghost btn-sm" style={{ justifyContent: 'flex-start', padding: '2px 6px', cursor: 'pointer', color: 'var(--hp-dim)' }}>
                        <Paperclip size={12} /> Add file
                        <input type="file" accept="application/pdf,image/*" style={{ display: 'none' }} onChange={(e) => { addFile(d, e.target.files?.[0]); e.target.value = ''; }} />
                      </label>
                    </div>
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button className="btn btn-ghost btn-sm" title="Edit" onClick={() => setDrawer(d)}><Pencil size={13} /></button>
                    {d.deletedAt
                      ? <button className="btn btn-ghost btn-sm" title="Restore" onClick={() => toggleRemoved(d, false)}><Undo2 size={13} /></button>
                      : <button className="btn btn-ghost btn-sm" title="Remove (can be restored)" style={{ color: RED }} onClick={() => toggleRemoved(d, true)}><Trash2 size={13} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {drawer && (
        <DocDrawer doc={drawer} entities={entities} unlock={unlock} notify={notify} onLocked={lock}
          onClose={() => setDrawer(null)} onSaved={(d) => { upsert(d); setDrawer(null); }} />
      )}
    </>
  );
}
